import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import { SERVICES, BUDGET_RANGES, TIMELINES, FINANCING_OPTIONS, SITE } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OptionCard = ({ selected, onClick, label, testid }) => (
    <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        aria-pressed={selected}
        className={`text-left px-6 py-5 border font-sans text-sm transition-all duration-300 ${selected ? "border-gold bg-forest text-chalk" : "border-graphite/20 text-graphite hover:border-graphite/60 bg-transparent"}`}
    >
        {label}
    </button>
);

export default function StartProject() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [sending, setSending] = useState(false);
    const [form, setForm] = useState({
        projectType: "", country: "", region: "", budget: "", timeline: "", financing: "",
        name: "", email: "", phone: "", message: "", consent: false, website: "",
    });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const steps = [
        {
            key: "projectType", title: "What are we building?", valid: !!form.projectType,
            node: (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {SERVICES.map((s) => (
                        <OptionCard key={s.slug} testid={`step-option-${s.slug}`} label={s.name} selected={form.projectType === s.name} onClick={() => set("projectType", s.name)} />
                    ))}
                </div>
            ),
        },
        {
            key: "location", title: "Where is the project?", valid: !!form.country && form.region.trim().length > 1,
            node: (
                <div className="space-y-8">
                    <div className="grid sm:grid-cols-2 gap-3">
                        {["United States", "Canada"].map((c) => (
                            <OptionCard key={c} testid={`step-country-${c === "Canada" ? "canada" : "us"}`} label={c} selected={form.country === c} onClick={() => set("country", c)} />
                        ))}
                    </div>
                    <div>
                        <label htmlFor="region" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">
                            State / Province & nearest city
                        </label>
                        <input
                            id="region"
                            data-testid="step-region-input"
                            type="text"
                            value={form.region}
                            onChange={(e) => set("region", e.target.value)}
                            placeholder="e.g. Oregon — Bend"
                            className="input-line"
                        />
                    </div>
                </div>
            ),
        },
        {
            key: "budget", title: "What budget should we design to?", valid: !!form.budget,
            node: (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {BUDGET_RANGES.map((b, i) => (
                        <OptionCard key={b} testid={`step-budget-${i}`} label={b} selected={form.budget === b} onClick={() => set("budget", b)} />
                    ))}
                </div>
            ),
        },
        {
            key: "timeline", title: "When do you want to begin?", valid: !!form.timeline,
            node: (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {TIMELINES.map((t, i) => (
                        <OptionCard key={t} testid={`step-timeline-${i}`} label={t} selected={form.timeline === t} onClick={() => set("timeline", t)} />
                    ))}
                </div>
            ),
        },
        {
            key: "financing", title: "How will the project be funded?", valid: !!form.financing,
            node: (
                <div className="grid sm:grid-cols-2 gap-3">
                    {FINANCING_OPTIONS.map((f, i) => (
                        <OptionCard key={f} testid={`step-financing-${i}`} label={f} selected={form.financing === f} onClick={() => set("financing", f)} />
                    ))}
                </div>
            ),
        },
        {
            key: "contact", title: "Where do we send the answer?", valid: form.name.trim().length > 1 && /.+@.+\..+/.test(form.email) && form.consent,
            node: (
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                        <label htmlFor="sp-name" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">Full name *</label>
                        <input id="sp-name" data-testid="form-name" type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-line" placeholder="Your name" />
                    </div>
                    <div>
                        <label htmlFor="sp-email" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">Email *</label>
                        <input id="sp-email" data-testid="form-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-line" placeholder="you@example.com" />
                    </div>
                    <div>
                        <label htmlFor="sp-phone" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">Phone</label>
                        <input id="sp-phone" data-testid="form-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-line" placeholder="Optional" />
                    </div>
                    <div className="sm:col-span-2">
                        <label htmlFor="sp-message" className="block font-sans text-[10px] font-medium tracking-[0.24em] uppercase text-slate mb-2">Anything else we should know?</label>
                        <textarea id="sp-message" data-testid="form-message" rows={3} value={form.message} onChange={(e) => set("message", e.target.value)} className="input-line resize-none" placeholder="Land, timelines, dreams — brief is fine." />
                    </div>
                    <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
                    <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer" data-testid="form-consent-label">
                        <input
                            type="checkbox"
                            data-testid="form-consent"
                            checked={form.consent}
                            onChange={(e) => set("consent", e.target.checked)}
                            className="mt-1 w-4 h-4 accent-[#D4AF37]"
                        />
                        <span className="font-sans text-xs leading-relaxed text-slate">
                            I agree that Dagaz Development may contact me about this brief by email or phone. *
                        </span>
                    </label>
                </div>
            ),
        },
    ];

    const current = steps[step];

    const submit = async () => {
        setSending(true);
        try {
            await axios.post(`${API}/project-inquiry`, form);
            navigate("/thank-you");
        } catch (e) {
            toast.error(`Could not send right now. Please email us directly at ${SITE.email}.`);
        } finally {
            setSending(false);
        }
    };

    const nextStep = () => {
        if (!current.valid) {
            toast.error("Please complete this step first.");
            return;
        }
        if (step < steps.length - 1) setStep(step + 1);
        else submit();
    };

    return (
        <>
            <Seo
                title="Start a Project — Tell Us What You're Building | Dagaz Development"
                description="A five-minute brief: project type, location, budget, timeline and financing. Read personally, answered within two business days. US & Canada."
                path="/start-a-project"
                jsonLd={[orgSchema, breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Start a Project", path: "/start-a-project" }])]}
            />
            <PageHeader
                eyebrow="Start a Project — 5 Minutes"
                titleLines={["Tell us what", "you're building."]}
                answer="A short brief, not a sales funnel. Six quick steps — project type, location, budget, timeline, financing and how to reach you. Every brief is read personally and answered within two business days."
                crumbs={[{ name: "Start a Project" }]}
            />
            <section className="px-6 md:px-12 lg:px-16 pb-28 md:pb-40 bg-chalk" data-testid="project-form">
                <div className="max-w-4xl">
                    <div className="flex items-center justify-between mb-4">
                        <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-slate" data-testid="form-progress-label">
                            Step {step + 1} of {steps.length}
                        </p>
                        <p className="font-sans text-[10px] font-medium tracking-[0.28em] uppercase text-gold">{form.projectType || "—"}</p>
                    </div>
                    <div className="h-px bg-graphite/15 mb-12 relative">
                        <motion.span
                            className="absolute left-0 top-0 h-px bg-gold"
                            animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
                            transition={{ duration: 0.6, ease: [0.65, 0, 0.35, 1] }}
                        />
                    </div>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={step}
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.45, ease: [0.65, 0, 0.35, 1] }}
                        >
                            <h2 className="font-serif font-light text-3xl md:text-5xl text-graphite mb-10">{current.title}</h2>
                            {current.node}
                        </motion.div>
                    </AnimatePresence>
                    <div className="mt-14 flex items-center gap-6">
                        {step > 0 && (
                            <button
                                type="button"
                                data-testid="form-back"
                                onClick={() => setStep(step - 1)}
                                className="font-sans text-xs font-medium tracking-[0.22em] uppercase text-slate hover:text-graphite transition-colors link-rule"
                            >
                                ← Back
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid="form-continue"
                            onClick={nextStep}
                            disabled={sending}
                            className="group inline-flex items-center gap-3 px-8 py-4 bg-forest text-chalk font-sans text-xs font-semibold tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-graphite disabled:opacity-50"
                        >
                            {sending ? "Sending…" : step === steps.length - 1 ? "Send Brief" : "Continue"}
                            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1.5" aria-hidden="true">→</span>
                        </button>
                    </div>
                </div>
            </section>
        </>
    );
}
